import type { MiceNextAction, MiceTraveler } from '@voll-intelligence/domain';
import type { ColumnDef, Table } from '@tanstack/react-table';
import { t } from '@web/i18n';
import { Checkbox, DataTable, IconButton, type DataTableSubRow } from '@web/ui/core';
import { JourneyCell, LegCell, NextActionCell, TravelerCell, ValueCell } from './traveler_cells';

/** O que as células leem a cada render (via `meta`): as colunas ficam fixas, fora do componente. */
interface TravelersTableMeta {
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  selected: ReadonlySet<string>;
  selectState: boolean | 'indeterminate';
  onSelect: (id: string, on: boolean) => void;
  onSelectAll: (on: boolean) => void;
  canAct: boolean;
  blocked: (action: MiceNextAction) => boolean;
  onAction: (traveler: MiceTraveler) => void;
}

interface TravelersTableProps extends TravelersTableMeta {
  travelers: MiceTraveler[];
  onOpen: (traveler: MiceTraveler) => void;
  /** Pedidos do viajante, uma célula por coluna; `null` sem pedido (a linha não expande). */
  renderSubRows: (traveler: MiceTraveler) => DataTableSubRow[] | null;
}

const metaOf = (table: Table<MiceTraveler>): TravelersTableMeta =>
  table.options.meta as TravelersTableMeta;

const COLUMNS: ColumnDef<MiceTraveler>[] = [
  {
    id: 'select',
    header: ({ table }) => {
      const m = metaOf(table);
      return (
        <Checkbox
          label={t('mice.table.selectAll')}
          checked={m.selectState}
          onChange={m.onSelectAll}
        />
      );
    },
    cell: ({ row, table }) => {
      const m = metaOf(table);
      return (
        // A linha inteira abre o painel; o checkbox só seleciona. A ilha só segura o clique (o do
        // teclado no checkbox também borbulha como clique): não é controle, sai da árvore.
        <span
          role="presentation"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Checkbox
            label={t('mice.table.selectRow', { row: row.original.rowNumber })}
            checked={m.selected.has(row.original.id)}
            onChange={(on) => {
              m.onSelect(row.original.id, on);
            }}
          />
        </span>
      );
    },
  },
  {
    id: 'expand',
    header: () => null,
    cell: ({ row, table }) => {
      // Só os pedidos abrem: antes da compra, o detalhe é o painel do viajante.
      if (row.original.orders.length === 0) return null;
      const m = metaOf(table);
      const open = m.expanded.has(row.original.id);
      const name = row.original.name ?? row.original.email;
      const label = t(open ? 'mice.tree.collapse' : 'mice.tree.expand', { name });
      return (
        <IconButton
          icon={open ? 'caret-down' : 'caret-right'}
          variant="ghost"
          size="xs"
          aria-expanded={open}
          aria-label={label}
          title={label}
          onClick={(event) => {
            event.stopPropagation();
            m.onToggle(row.original.id);
          }}
        />
      );
    },
  },
  {
    id: 'traveler',
    header: () => t('mice.travelers.col.traveler'),
    cell: ({ row }) => <TravelerCell traveler={row.original} />,
  },
  {
    id: 'journey',
    header: () => t('mice.travelers.col.journey'),
    cell: ({ row }) => <JourneyCell traveler={row.original} />,
  },
  {
    id: 'outbound',
    header: () => t('mice.travelers.col.outbound'),
    cell: ({ row }) => <LegCell traveler={row.original} direction="OUTWARD" />,
  },
  {
    id: 'inbound',
    header: () => t('mice.travelers.col.inbound'),
    cell: ({ row }) => <LegCell traveler={row.original} direction="RETURN" />,
  },
  {
    id: 'value',
    header: () => t('mice.travelers.col.value'),
    cell: ({ row }) => <ValueCell traveler={row.original} />,
  },
  {
    id: 'next',
    header: () => t('mice.travelers.col.next'),
    cell: ({ row, table }) => {
      const m = metaOf(table);
      return (
        <NextActionCell
          traveler={row.original}
          canAct={m.canAct}
          blocked={m.blocked}
          onAction={m.onAction}
        />
      );
    },
  },
];

export function TravelersTable({
  travelers,
  onOpen,
  renderSubRows,
  ...meta
}: Readonly<TravelersTableProps>) {
  return (
    <DataTable
      columns={COLUMNS}
      data={travelers}
      getRowId={(traveler) => traveler.id}
      onRowClick={onOpen}
      emptyMessage={t('mice.travelers.empty')}
      expandedRowIds={meta.expanded}
      renderSubRows={renderSubRows}
      dense
      meta={meta}
    />
  );
}
